import { useEffect, useState, useRef } from 'react';
import { get } from 'lodash';
import {
  Box, Card, Container, Grid, LinearProgress, Stack, Typography, Chip,
} from '@mui/material';
import { Icon } from '@iconify/react';
import Page from '../../components/Page';
import { getSystemHealth, getDiskUsage, getMongoStats } from '../../axiosLib/services/system/admin';

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const getColor = (pct) => {
  if (pct < 50) return '#22c55e';
  if (pct < 75) return '#f59e0b';
  if (pct < 90) return '#f97316';
  return '#ef4444';
};

const GaugeCard = ({ title, icon, value, max, unit, percent, subtitle, color }) => {
  const pct = percent || (max ? Math.round((value / max) * 100 * 10) / 10 : 0);
  const gaugeColor = color || getColor(pct);

  return (
    <Card sx={{
      p: 3, borderRadius: 3, border: '1px solid rgba(99,102,241,0.06)',
      background: '#fff', position: 'relative', overflow: 'hidden',
      transition: 'box-shadow 0.3s',
      '&:hover': { boxShadow: '0 8px 24px rgba(99,102,241,0.1)' },
    }}>
      {/* Top accent bar */}
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${gaugeColor}, ${gaugeColor}88)`,
      }} />

      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <Box sx={{
          width: 42, height: 42, borderRadius: 2, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: `${gaugeColor}15`,
        }}>
          <Icon icon={icon} style={{ fontSize: 22, color: gaugeColor }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e1b4b' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>{subtitle}</Typography>
          )}
        </Box>
      </Stack>

      {/* Circular gauge */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ position: 'relative', width: 72, height: 72 }}>
          <svg viewBox="0 0 36 36" width="72" height="72">
            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none" stroke="#e5e7eb" strokeWidth="3" />
            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none" stroke={gaugeColor} strokeWidth="3"
              strokeDasharray={`${pct}, 100`}
              strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 0.6s ease' }} />
          </svg>
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexDirection: 'column',
          }}>
            <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: gaugeColor, lineHeight: 1 }}>
              {pct}%
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>
            Used: <strong>{unit ? `${value} ${unit}` : formatBytes(value)}</strong>
          </Typography>
          {max !== undefined && (
            <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>
              Total: {unit ? `${max} ${unit}` : formatBytes(max)}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default function SystemHealthMonitor() {
  const [health, setHealth] = useState(null);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [diskUsage, setDiskUsage] = useState(null);
  const [mongoStats, setMongoStats] = useState(null);
  const intervalRef = useRef(null);

  const fetchHealth = () => {
    getSystemHealth((err, data) => {
      if (!err && data.success) {
        setHealth(data);
        setError('');
        setLastUpdate(new Date());
      } else {
        setError(err || 'Failed to fetch health data');
      }
    });
  };

  useEffect(() => {
    fetchHealth();
    // Disk usage is cached 2 min server-side, no need to poll every second
    getDiskUsage((err, data) => {
      if (!err && data.success) setDiskUsage(data);
    });
    getMongoStats((err, data) => {
      if (!err && data.success) setMongoStats(data);
    });
    intervalRef.current = setInterval(fetchHealth, 1000);
    // Refresh disk usage & mongo stats every 2 minutes
    const slowInterval = setInterval(() => {
      getDiskUsage((err, data) => {
        if (!err && data.success) setDiskUsage(data);
      });
      getMongoStats((err, data) => {
        if (!err && data.success) setMongoStats(data);
      });
    }, 120000);
    return () => { clearInterval(intervalRef.current); clearInterval(slowInterval); };
  }, []);

  const cpu = get(health, 'cpu', {});
  const mem = get(health, 'memory', {});
  const disk = get(health, 'disk', {});
  const uptime = get(health, 'uptime', {});
  const nodeProc = get(health, 'nodeProcess', {});
  const sys = get(health, 'system', {});
  const topProcs = get(health, 'topProcesses', []);

  return (
    <Page title="System Health Monitor">
      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e1b4b' }}>
              ⚙️ System Health Monitor
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
              <Chip size="small" icon={<Icon icon="mdi:circle" style={{ fontSize: 8, color: error ? '#ef4444' : '#22c55e' }} />}
                label={error ? 'Connection Error' : 'Live'}
                sx={{
                  background: error ? '#fef2f2' : '#f0fdf4',
                  color: error ? '#ef4444' : '#22c55e',
                  fontWeight: 700, fontSize: '0.7rem',
                }}
              />
              <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>
                Refreshing every 1s {lastUpdate && `• Last: ${lastUpdate.toLocaleTimeString()}`}
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {sys.hostname && (
              <Chip label={`🖥 ${sys.hostname}`} size="small"
                sx={{ background: '#f1f5f9', fontWeight: 600, fontSize: '0.72rem' }} />
            )}
            {sys.platform && (
              <Chip label={`${sys.platform}/${sys.arch}`} size="small"
                sx={{ background: '#f1f5f9', fontWeight: 600, fontSize: '0.72rem' }} />
            )}
            {sys.nodeVersion && (
              <Chip label={`Node ${sys.nodeVersion}`} size="small"
                sx={{ background: '#ecfdf5', color: '#059669', fontWeight: 600, fontSize: '0.72rem' }} />
            )}
          </Stack>
        </Stack>

        {error && !health && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Icon icon="mdi:alert-circle-outline" style={{ fontSize: 64, color: '#ef4444' }} />
            <Typography sx={{ color: '#ef4444', mt: 2, fontWeight: 600 }}>
              {typeof error === 'string' ? error : 'Unable to connect. Make sure you have SUPER_ADMIN access.'}
            </Typography>
          </Box>
        )}

        {health && (
          <>
            {/* ═══════ MAIN GAUGES ═══════ */}
            <Grid container spacing={2.5} mb={3}>
              <Grid item xs={12} sm={6} md={3}>
                <GaugeCard
                  title="CPU Usage" icon="mdi:chip"
                  value={get(cpu, 'usage', 0)} percent={get(cpu, 'usage', 0)}
                  subtitle={`${get(cpu, 'cores', 0)} cores • ${(get(cpu, 'model', '')).substring(0, 30)}`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <GaugeCard
                  title="Memory (RAM)" icon="mdi:memory"
                  value={get(mem, 'used', 0)} max={get(mem, 'total', 1)}
                  percent={get(mem, 'usagePercent', 0)}
                  subtitle={`${formatBytes(get(mem, 'free', 0))} free`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <GaugeCard
                  title="Disk Space" icon="mdi:harddisk"
                  value={get(disk, 'used', 0)} max={get(disk, 'total', 1)}
                  percent={get(disk, 'percent', 0)}
                  subtitle={`${formatBytes(get(disk, 'available', 0))} available`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{
                  p: 3, borderRadius: 3, border: '1px solid rgba(99,102,241,0.06)',
                  background: '#fff', position: 'relative', overflow: 'hidden',
                }}>
                  <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                    background: 'linear-gradient(90deg, #6366f1, #a78bfa)',
                  }} />
                  <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                    <Box sx={{
                      width: 42, height: 42, borderRadius: 2, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(99,102,241,0.1)',
                    }}>
                      <Icon icon="mdi:clock-outline" style={{ fontSize: 22, color: '#6366f1' }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e1b4b' }}>
                      Uptime
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontWeight: 900, fontSize: '1.5rem', color: '#1e1b4b', lineHeight: 1.2 }}>
                    {get(uptime, 'days', 0)}d {get(uptime, 'hours', 0)}h {get(uptime, 'minutes', 0)}m
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#888', mt: 0.5 }}>
                    Node.js: {Math.floor(get(nodeProc, 'uptime', 0) / 3600)}h {Math.floor((get(nodeProc, 'uptime', 0) % 3600) / 60)}m
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {/* ═══════ CPU PER CORE + LOAD AVG ═══════ */}
            <Grid container spacing={2.5} mb={3}>
              <Grid item xs={12} md={8}>
                <Card sx={{
                  p: 3, borderRadius: 3, border: '1px solid rgba(99,102,241,0.06)',
                }}>
                  <Typography sx={{ fontWeight: 700, color: '#1e1b4b', mb: 2, fontSize: '0.9rem' }}>
                    CPU Per Core
                  </Typography>
                  <Grid container spacing={1}>
                    {get(cpu, 'perCore', []).map((core) => (
                      <Grid item xs={6} sm={4} md={3} key={core.core}>
                        <Box sx={{ mb: 1 }}>
                          <Stack direction="row" justifyContent="space-between" mb={0.3}>
                            <Typography sx={{ fontSize: '0.7rem', color: '#666', fontWeight: 600 }}>
                              Core {core.core}
                            </Typography>
                            <Typography sx={{
                              fontSize: '0.7rem', fontWeight: 800,
                              color: getColor(core.usage),
                            }}>
                              {core.usage}%
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate" value={Math.min(core.usage, 100)}
                            sx={{
                              height: 6, borderRadius: 3, backgroundColor: '#f1f5f9',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                background: `linear-gradient(90deg, ${getColor(core.usage)}, ${getColor(core.usage)}cc)`,
                                transition: 'transform 0.6s ease',
                              },
                            }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{
                  p: 3, borderRadius: 3, border: '1px solid rgba(99,102,241,0.06)', height: '100%',
                }}>
                  <Typography sx={{ fontWeight: 700, color: '#1e1b4b', mb: 2, fontSize: '0.9rem' }}>
                    Load Average
                  </Typography>
                  {['1min', '5min', '15min'].map((key) => {
                    const val = get(cpu, `loadAvg.${key}`, 0);
                    const maxLoad = get(cpu, 'cores', 1);
                    const pct = Math.min(Math.round((val / maxLoad) * 100), 100);
                    return (
                      <Box key={key} sx={{ mb: 2 }}>
                        <Stack direction="row" justifyContent="space-between" mb={0.3}>
                          <Typography sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>
                            {key}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: getColor(pct) }}>
                            {val}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate" value={pct}
                          sx={{
                            height: 8, borderRadius: 3, backgroundColor: '#f1f5f9',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                              background: `linear-gradient(90deg, ${getColor(pct)}, ${getColor(pct)}cc)`,
                              transition: 'transform 0.6s ease',
                            },
                          }}
                        />
                      </Box>
                    );
                  })}

                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                    <Typography sx={{ fontWeight: 700, color: '#1e1b4b', mb: 1, fontSize: '0.82rem' }}>
                      Node.js Memory
                    </Typography>
                    <Stack spacing={0.5}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: '0.72rem', color: '#666' }}>RSS</Typography>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{formatBytes(get(nodeProc, 'rss', 0))}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: '0.72rem', color: '#666' }}>Heap Used</Typography>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{formatBytes(get(nodeProc, 'heapUsed', 0))}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: '0.72rem', color: '#666' }}>Heap Total</Typography>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{formatBytes(get(nodeProc, 'heapTotal', 0))}</Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            </Grid>

            {/* ═══════ TOP PROCESSES ═══════ */}
            <Card sx={{
              p: 3, borderRadius: 3, border: '1px solid rgba(99,102,241,0.06)', mb: 3,
            }}>
              <Typography sx={{ fontWeight: 700, color: '#1e1b4b', mb: 2, fontSize: '0.9rem' }}>
                Top Processes (by CPU)
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{
                  display: 'grid', gridTemplateColumns: '80px 100px 100px 1fr',
                  gap: 0, fontSize: '0.75rem',
                }}>
                  {/* Header */}
                  {['PID', 'CPU %', 'MEM %', 'Command'].map((h) => (
                    <Box key={h} sx={{
                      p: 1, fontWeight: 700, color: '#6366f1', borderBottom: '2px solid #e5e7eb',
                      background: '#f8fafc',
                    }}>
                      {h}
                    </Box>
                  ))}
                  {/* Rows */}
                  {topProcs.map((proc, i) => (
                    [proc.pid, proc.cpu + '%', proc.mem + '%', proc.command].map((val, j) => (
                      <Box key={`${i}-${j}`} sx={{
                        p: 1, borderBottom: '1px solid #f1f5f9',
                        fontWeight: j === 0 ? 700 : 400,
                        color: j === 1 && proc.cpu > 50 ? '#ef4444' : '#333',
                        fontFamily: 'monospace', fontSize: '0.73rem',
                      }}>
                        {val}
                      </Box>
                    ))
                  ))}
                </Box>
              </Box>
            </Card>

            {/* ═══════ NETWORK ═══════ */}
            {Object.keys(get(health, 'network', {})).length > 0 && (
              <Card sx={{
                p: 3, borderRadius: 3, border: '1px solid rgba(99,102,241,0.06)',
              }}>
                <Typography sx={{ fontWeight: 700, color: '#1e1b4b', mb: 1.5, fontSize: '0.9rem' }}>
                  Network Interfaces
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {Object.entries(get(health, 'network', {})).map(([name, ip]) => (
                    <Chip key={name} label={`${name}: ${ip}`} size="small"
                      sx={{
                        background: 'rgba(99,102,241,0.08)', color: '#4338ca',
                        fontWeight: 600, fontSize: '0.72rem', fontFamily: 'monospace',
                      }}
                    />
                  ))}
                </Stack>
              </Card>
            )}

            {/* ═══════ DIRECTORY STORAGE ═══════ */}
            {diskUsage && (() => {
              const allDirs = diskUsage.directories || [];
              const coreDirs = allDirs.filter(d => !d.name.startsWith('/public/'));
              const publicDirs = allDirs.filter(d => d.name.startsWith('/public/')).sort((a, b) => b.size - a.size);
              const totalRoot = (allDirs[0] || {}).size || 1;
              const coreColors = ['#6366f1', '#f59e0b', '#22c55e'];
              const pubColors = ['#06b6d4', '#8b5cf6', '#ec4899', '#f97316', '#14b8a6', '#ef4444', '#3b82f6', '#84cc16'];

              const DirTile = ({ dir, color, rootSize }) => {
                const pctOfRoot = dir.name === 'Project Root (.)' ? 100 : Math.round((dir.size / rootSize) * 100 * 10) / 10;
                return (
                  <Box sx={{
                    p: 2, borderRadius: 2, border: `1px solid ${color}15`,
                    background: `${color}03`, height: '100%',
                  }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                      <Icon icon={dir.name.includes('Root') ? 'mdi:folder-home' : 'mdi:folder'}
                        style={{ fontSize: 20, color }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e1b4b' }} noWrap>
                          {dir.name}
                        </Typography>
                      </Box>
                    </Stack>
                    {!dir.exists ? (
                      <Typography sx={{ fontSize: '0.72rem', color: '#999', fontStyle: 'italic' }}>
                        Directory not found
                      </Typography>
                    ) : (
                      <>
                        <Typography sx={{ fontWeight: 900, fontSize: '1.3rem', color, lineHeight: 1.2 }}>
                          {formatBytes(dir.size)}
                        </Typography>
                        <Stack direction="row" spacing={1} mt={0.5} mb={1}>
                          {dir.fileCount !== undefined && (
                            <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>
                              {dir.fileCount.toLocaleString()} files
                            </Typography>
                          )}
                        </Stack>
                        {dir.name !== 'Project Root (.)' && (
                          <>
                            <LinearProgress
                              variant="determinate" value={Math.min(pctOfRoot, 100)}
                              sx={{
                                height: 6, borderRadius: 3, backgroundColor: '#f1f5f9',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 3,
                                  background: `linear-gradient(90deg, ${color}, ${color}99)`,
                                },
                              }}
                            />
                            <Typography sx={{ fontSize: '0.65rem', color: '#888', mt: 0.3 }}>
                              {pctOfRoot}% of project
                            </Typography>
                          </>
                        )}
                      </>
                    )}
                  </Box>
                );
              };

              return (
                <Card sx={{
                  p: 3, borderRadius: 3, border: '1px solid rgba(99,102,241,0.06)', mt: 3,
                }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 2, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(99,102,241,0.1)',
                    }}>
                      <Icon icon="mdi:folder-multiple" style={{ fontSize: 20, color: '#6366f1' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#1e1b4b', fontSize: '0.9rem' }}>
                        Directory Storage Breakdown
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>
                        {diskUsage.cached ? '📦 Cached (2 min TTL)' : '🔄 Fresh scan'}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Core Directories */}
                  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#6366f1', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Core Directories
                  </Typography>
                  <Grid container spacing={2} mb={3}>
                    {coreDirs.map((dir, i) => (
                      <Grid item xs={12} sm={6} md={4} key={dir.name}>
                        <DirTile dir={dir} color={coreColors[i % coreColors.length]} rootSize={totalRoot} />
                      </Grid>
                    ))}
                  </Grid>

                  {/* Public Subdirectories */}
                  {publicDirs.length > 0 && (
                    <>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#06b6d4', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        /public Subdirectories ({publicDirs.length})
                      </Typography>
                      <Grid container spacing={1.5}>
                        {publicDirs.map((dir, i) => (
                          <Grid item xs={12} sm={6} md={3} key={dir.name}>
                            <DirTile dir={dir} color={pubColors[i % pubColors.length]} rootSize={totalRoot} />
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </Card>
              );
            })()}

            {/* ═══════ MONGODB HEALTH ═══════ */}
            {mongoStats && (() => {
              const db = mongoStats.database || {};
              const srv = mongoStats.server || {};
              const cols = mongoStats.topCollections || [];
              const conn = srv.connections || {};
              const ops = srv.opcounters || {};
              const srvMem = srv.mem || {};
              const compressionRatio = db.dataSize && db.storageSize ? ((1 - db.storageSize / db.dataSize) * 100).toFixed(1) : 0;
              const srvUpH = Math.floor((srv.uptime || 0) / 3600);
              const srvUpM = Math.floor(((srv.uptime || 0) % 3600) / 60);

              return (
                <Card sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(99,102,241,0.06)', mt: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
                    <Box sx={{
                      width: 40, height: 40, borderRadius: 2, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(34,197,94,0.1)',
                    }}>
                      <Icon icon="simple-icons:mongodb" style={{ fontSize: 22, color: '#22c55e' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#1e1b4b', fontSize: '0.9rem' }}>
                        MongoDB Health — {db.name || 'N/A'}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>
                          {mongoStats.cached ? '📦 Cached (2 min)' : '🔄 Fresh'}
                        </Typography>
                        {srv.version && srv.version !== 'N/A' && (
                          <Chip label={`v${srv.version}`} size="small"
                            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, background: '#ecfdf5', color: '#059669' }} />
                        )}
                      </Stack>
                    </Box>
                  </Stack>

                  {/* Database overview metrics */}
                  <Grid container spacing={2} mb={3}>
                    {[
                      { label: 'Data Size (Uncompressed)', value: formatBytes(db.dataSize), icon: 'mdi:database', color: '#6366f1' },
                      { label: 'Storage Size (Compressed)', value: formatBytes(db.storageSize), icon: 'mdi:zip-box', color: '#22c55e' },
                      { label: 'Index Size', value: formatBytes(db.indexSize), icon: 'mdi:flash', color: '#f59e0b' },
                      { label: 'Total On-Disk', value: formatBytes(db.totalSize), icon: 'mdi:harddisk', color: '#06b6d4' },
                    ].map((m) => (
                      <Grid item xs={6} sm={3} key={m.label}>
                        <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${m.color}15`, background: `${m.color}03` }}>
                          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                            <Icon icon={m.icon} style={{ fontSize: 18, color: m.color }} />
                            <Typography sx={{ fontSize: '0.7rem', color: '#666', fontWeight: 600 }}>{m.label}</Typography>
                          </Stack>
                          <Typography sx={{ fontWeight: 900, fontSize: '1.2rem', color: m.color }}>{m.value}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Quick stats row */}
                  <Grid container spacing={2} mb={3}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 1.5, borderRadius: 2, background: '#f8fafc', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>Collections</Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1e1b4b' }}>{db.collections || 0}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 1.5, borderRadius: 2, background: '#f8fafc', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>Documents</Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1e1b4b' }}>{(db.documents || 0).toLocaleString()}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 1.5, borderRadius: 2, background: '#f8fafc', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>Compression</Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#22c55e' }}>{compressionRatio}%</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 1.5, borderRadius: 2, background: '#f8fafc', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>Server Uptime</Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#6366f1' }}>{srvUpH}h {srvUpM}m</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Connections & Opcounters */}
                  <Grid container spacing={2} mb={3}>
                    {Object.keys(conn).length > 0 && (
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ p: 2, borderRadius: 2, border: '1px solid rgba(99,102,241,0.06)' }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#6366f1', mb: 1 }}>Connections</Typography>
                          <Stack spacing={0.5}>
                            {['current', 'available', 'totalCreated'].filter(k => conn[k] !== undefined).map(k => (
                              <Stack direction="row" justifyContent="space-between" key={k}>
                                <Typography sx={{ fontSize: '0.72rem', color: '#666', textTransform: 'capitalize' }}>{k}</Typography>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{(conn[k] || 0).toLocaleString()}</Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </Box>
                      </Grid>
                    )}
                    {Object.keys(ops).length > 0 && (
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ p: 2, borderRadius: 2, border: '1px solid rgba(99,102,241,0.06)' }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#f59e0b', mb: 1 }}>Op Counters</Typography>
                          <Stack spacing={0.5}>
                            {['insert', 'query', 'update', 'delete', 'getmore', 'command'].filter(k => ops[k] !== undefined).map(k => (
                              <Stack direction="row" justifyContent="space-between" key={k}>
                                <Typography sx={{ fontSize: '0.72rem', color: '#666', textTransform: 'capitalize' }}>{k}</Typography>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{(ops[k] || 0).toLocaleString()}</Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </Box>
                      </Grid>
                    )}
                    {Object.keys(srvMem).length > 0 && (
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ p: 2, borderRadius: 2, border: '1px solid rgba(99,102,241,0.06)' }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#22c55e', mb: 1 }}>Server Memory</Typography>
                          <Stack spacing={0.5}>
                            {['resident', 'virtual', 'mapped'].filter(k => srvMem[k] !== undefined).map(k => (
                              <Stack direction="row" justifyContent="space-between" key={k}>
                                <Typography sx={{ fontSize: '0.72rem', color: '#666', textTransform: 'capitalize' }}>{k}</Typography>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{srvMem[k]} MB</Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {/* Top collections table */}
                  {cols.length > 0 && (
                    <>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#06b6d4', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Top Collections (by storage)
                      </Typography>
                      <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{
                          display: 'grid', gridTemplateColumns: '1fr 80px 90px 90px 90px',
                          gap: 0, fontSize: '0.73rem',
                        }}>
                          {['Collection', 'Docs', 'Data', 'Storage', 'Indexes'].map((h) => (
                            <Box key={h} sx={{
                              p: 1, fontWeight: 700, color: '#06b6d4', borderBottom: '2px solid #e5e7eb',
                              background: '#f8fafc',
                            }}>{h}</Box>
                          ))}
                          {cols.map((c, i) => (
                            [c.name, (c.count || 0).toLocaleString(), formatBytes(c.size), formatBytes(c.storageSize), formatBytes(c.indexSize)].map((val, j) => (
                              <Box key={`${i}-${j}`} sx={{
                                p: 1, borderBottom: '1px solid #f1f5f9',
                                fontWeight: j === 0 ? 600 : 400,
                                color: '#333', fontFamily: j === 0 ? 'inherit' : 'monospace',
                              }}>{val}</Box>
                            ))
                          ))}
                        </Box>
                      </Box>
                    </>
                  )}
                </Card>
              );
            })()}
          </>
        )}
      </Container>
    </Page>
  );
}
